import { useState } from 'react'
import { Stack, Text, Group, Box, ScrollArea, Loader, Badge, ActionIcon, Collapse, Paper } from '@mantine/core'
import { IconFileText, IconFileTypePdf, IconFileTypeDocx, IconDownload, IconChevronDown, IconChevronUp, IconInbox } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { storyService } from '../../services/storyService'
import { MarkdownRenderer } from '../common/MarkdownRenderer'
import { PanelTitle } from '../common/PanelTitle'

function fileTypeMeta(contentType = '') {
  if (contentType?.includes('pdf')) return { label: 'PDF', color: 'red', Icon: IconFileTypePdf }
  if (contentType?.includes('wordprocessingml')) return { label: 'DOCX', color: 'blue', Icon: IconFileTypeDocx }
  return { label: 'TXT', color: 'gray', Icon: IconFileText }
}

function DocumentCard({ doc }) {
  const [expanded, setExpanded] = useState(false)
  const { label, color, Icon } = fileTypeMeta(doc.file_content_type)
  const hasSummary = !!doc.notes?.trim()

  return (
    <Box
      style={{
        background: 'rgba(10, 22, 40, 0.6)',
        border: '1px solid rgba(0, 255, 255, 0.1)',
        borderRadius: 4,
        padding: '10px 12px',
        transition: 'border-color 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.25)' }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.1)' }}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Icon size={16} color="rgba(0, 229, 255, 0.6)" style={{ flexShrink: 0 }} />
          <Text
            size="sm"
            fw={600}
            c="cyan.3"
            ff="var(--bw-heading-font)"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: 'calc(0.75rem * var(--bw-content-scale, 1))',
              letterSpacing: 'var(--bw-heading-letter-spacing, 0.05em)',
            }}
            title={doc.name}
          >
            {doc.name}
          </Text>
          <Badge size="xs" variant="light" color={color} style={{ flexShrink: 0 }}>
            {label}
          </Badge>
        </Group>

        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          {hasSummary && (
            <ActionIcon
              size="sm"
              variant="subtle"
              color="cyan"
              onClick={() => setExpanded(prev => !prev)}
              title={expanded ? 'Hide summary' : 'Show summary'}
              style={{ opacity: 0.7 }}
            >
              {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            </ActionIcon>
          )}
          {doc.file_url && (
            <ActionIcon
              size="sm"
              variant="subtle"
              color="cyan"
              component="a"
              href={doc.file_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Download"
              style={{ transition: 'box-shadow 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 6px rgba(0, 229, 255, 0.6)' }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}
            >
              <IconDownload size={14} />
            </ActionIcon>
          )}
        </Group>
      </Group>

      {hasSummary && (
        <Collapse in={expanded}>
          <Paper
            mt="xs"
            p="xs"
            className="glass"
            style={{
              background: 'rgba(6, 14, 26, 0.7)',
              border: '1px solid rgba(0, 255, 255, 0.08)',
            }}
          >
            <MarkdownRenderer
              content={doc.notes}
              style={{ fontSize: 'calc(0.8rem * var(--bw-content-scale, 1))', color: 'var(--mantine-color-gray-4)', lineHeight: 1.5 }}
            />
          </Paper>
        </Collapse>
      )}
    </Box>
  )
}

export function DocumentsPanel({ storyId, sessionRole = null, rolesRequired = false, hasSession = true }) {
  // The server filters documents by the player's session role. Key the query on
  // the resolved role so a list fetched before the role was known (which returns
  // only the shared docs) can't be served from cache once the role resolves —
  // and on replay with a different role, the new role's docs load instead of the
  // prior role's stale list.
  //
  // When the story requires a role, hold the fetch until the role is known so we
  // never cache a roleless (shared-only) response under this story — BUT only for
  // a viewer who actually has a play session and whose role is still resolving.
  // A viewer with no session (a facilitator previewing the story, or an admin not
  // playing) will never have a role, so holding the fetch would strand them with
  // an empty panel forever. They should fetch immediately and get the shared docs
  // the server returns for the roleless case.
  const { data: documents = [], isLoading, isError } = useQuery({
    queryKey: ['storyDocuments', storyId, sessionRole],
    queryFn: () => storyService.getStoryDocuments(storyId),
    enabled: !!storyId && (!rolesRequired || !!sessionRole || !hasSession),
    // Documents can be release-gated to a thread: a doc appears the moment its
    // session is released by the facilitator. The server decides visibility, so
    // we just need to refetch periodically (and on refocus) for newly-unlocked
    // docs to show up live, without the player reloading mid-class.
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  })

  return (
    <Stack
      gap="xs"
      style={{
        height: '100%',
        background: 'linear-gradient(to top, rgba(6, 14, 26, 0.96), rgba(10, 22, 40, 0.92))',
        borderTop: '1px solid rgba(0, 255, 255, 0.1)',
      }}
      px="lg"
      pt="md"
      pb="md"
    >
      <PanelTitle>DOCUMENTS</PanelTitle>

      {isLoading && (
        <Group gap="sm" mt={4}>
          <Loader size="xs" color="cyan" />
          <Text size="sm" c="dimmed" fs="italic">Loading documents...</Text>
        </Group>
      )}

      {isError && (
        <Text size="xs" c="red.4" fs="italic">
          Failed to load documents.
        </Text>
      )}

      {!isLoading && !isError && documents.length === 0 && (
        <Group gap="xs" mt={4} style={{ opacity: 0.5 }}>
          <IconInbox size={14} color="rgba(255,255,255,0.35)" />
          <Text size="xs" c="dimmed" fs="italic">No documents available.</Text>
        </Group>
      )}

      {!isLoading && documents.length > 0 && (
        <ScrollArea
          style={{ flex: 1, minHeight: 0 }}
          scrollbarSize={4}
          type="auto"
        >
          <Stack gap={6} pb={4}>
            {documents.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} />
            ))}
          </Stack>
        </ScrollArea>
      )}
    </Stack>
  )
}
